// src/utils/platform.ts
//
// Rozpoznání platformy (iOS / Android / nativní obal) a odkazy do mapových
// aplikací. Nahrazuje vlastní kopie detekce z MapViewer, PaymentRace,
// UserDashboard a ShootingRanges.
//
// Mobilní aplikace LKS je Expo WebView obal webu. Novější obal se hlásí
// značkou "LKSApp/x.y.z" v User-Agentu a před skripty stránky nastaví
// window.__LKS_NATIVE__ = true; starší obal značku nemá a pozná se jen podle
// tokenu "wv" (Android WebView, v praxi Chrome 80).
//
// User-Agent mimo tenhle modul ještě čtou (při změně detekce upravit i je):
//  - inline skript v public/index.html (--safe-top) – běží před bundlem,
//    proto tenhle modul importovat nemůže; drží stejná pravidla;
//  - src/utils/audio.ts – isAndroid (shodný s isAndroid níže) a
//    isSamsungBrowser pro Android TTS frontu a beepy rozhodčích; převést
//    sem v kroku 12 redesignu (rozhodčí, audio).
//
// Funkce berou volitelný User-Agent (testy); výchozí je navigator.userAgent.
// Starý obal (bez značky LKSApp) neumí otevřít odkaz mimo aplikaci –
// obrazovky se ptají canOpenExternalLinks() a nabídnou zkopírování.

declare global {
  interface Window {
    /** Nastavuje nativní obal před načtením stránky (injectedJavaScriptBeforeContentLoaded). */
    __LKS_NATIVE__?: boolean;
  }
}

/** User-Agent aktuálního prohlížeče; mimo prohlížeč prázdný řetězec. */
function currentUserAgent(): string {
  return typeof navigator !== "undefined" &&
    typeof navigator.userAgent === "string"
    ? navigator.userAgent
    : "";
}

/**
 * iPhone, iPod a iPad se „mobilním" UA. iPadOS v Safari se hlásí jako Mac
 * a vrací false – stejně jako všechny dosavadní kopie detekce.
 */
export function isIOS(ua: string = currentUserAgent()): boolean {
  return /iPhone|iPad|iPod/i.test(ua);
}

/** Android; iOS zařízení, jejichž UA slovo „Android" náhodou obsahuje, ne. */
export function isAndroid(ua: string = currentUserAgent()): boolean {
  return /Android/i.test(ua) && !isIOS(ua);
}

/** Telefon nebo tablet s iOS či Androidem. */
export function isMobileDevice(ua: string = currentUserAgent()): boolean {
  return isIOS(ua) || isAndroid(ua);
}

/**
 * Aplikace běží v nativním obalu LKS: značka "LKSApp/" v UA, nebo proměnná
 * od obalu (ta se čte vždy z window, i když test předá vlastní UA).
 */
export function isNativeApp(ua: string = currentUserAgent()): boolean {
  if (/LKSApp\//.test(ua)) return true;
  return typeof window !== "undefined" && window.__LKS_NATIVE__ === true;
}

/**
 * Starší obal bez značky LKSApp – Android WebView s tokenem "wv" (typicky
 * Chrome 80). Novější obal má "wv" v UA také, proto se vylučuje.
 */
export function isOldWebView(ua: string = currentUserAgent()): boolean {
  return /\bwv\b/.test(ua) && !isNativeApp(ua);
}

/** Webová aplikace spuštěná z plochy iPhonu (navigator.standalone) nebo jako PWA. */
function isStandaloneApp(): boolean {
  if (typeof navigator !== "undefined" && (navigator as { standalone?: boolean }).standalone === true) {
    return true;
  }
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches
    );
  } catch {
    return false;
  }
}

/**
 * Otevře prostředí odkaz s target=_blank (nebo window.open) mimo aplikaci?
 * Prohlížeč a PWA ano; nový obal LKS (značka LKSApp/) ho předá systému
 * (onOpenWindow → prohlížeč, PDF aplikace, mapy). Starý obal bez značky ne:
 *  - Android (token "wv"): odkaz tiše nic neudělá,
 *  - iOS (WKWebView s výchozím UA – končí „Mobile/…", bez „Safari/" a bez
 *    přípon vestavěných prohlížečů jiných aplikací): stránka se načte místo
 *    aplikace a cesta zpět nevede.
 * Tam se místo odkazu nabízí jeho zkopírování.
 */
export function canOpenExternalLinks(ua: string = currentUserAgent()): boolean {
  if (isNativeApp(ua)) return true;
  if (isOldWebView(ua)) return false;
  if (isIOS(ua) && /\bMobile\/\w+$/.test(ua.trim()) && !isStandaloneApp()) return false;
  return true;
}

/**
 * Klíč Google Maps (JS i Embed API) podle platformy: iOS/Android dostanou
 * REACT_APP_GOOGLE_MAPS_KEY_MOBILE, ostatní REACT_APP_GOOGLE_MAPS_KEY_WEB.
 * Hodnoty vkládá CRA při buildu z env; chybějící klíč = undefined.
 */
export function googleMapsApiKey(
  ua: string = currentUserAgent()
): string | undefined {
  const key = isMobileDevice(ua)
    ? process.env.REACT_APP_GOOGLE_MAPS_KEY_MOBILE
    : process.env.REACT_APP_GOOGLE_MAPS_KEY_WEB;
  return key ? key : undefined;
}

// ── Souřadnice a odkazy do mapových aplikací ──────────────────────

/** Číslo souřadnice z dokumentu (number nebo číselný řetězec); jinak null. */
export function toCoordinate(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Použitelná dvojice souřadnic WGS-84: konečná čísla v rozsahu. Bod 0,0
 * („nulový ostrov" v Guinejském zálivu) bere jako nevyplněné hodnoty.
 */
export function isValidLatLng(lat: unknown, lng: unknown): boolean {
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  return !(lat === 0 && lng === 0);
}

export interface MapPlace {
  /** Zeměpisná šířka a délka; bez platné dvojice se hledá podle textu. */
  lat?: number | null;
  lng?: number | null;
  /**
   * Místo z pole mapLink střelnice (mapUrl závodu): text pro vyhledání
   * (adresa / položka našeptávače), nebo celý odkaz na mapu (http/https).
   */
  query?: string | null;
  /** Název místa (střelnice) – záložní text, když query chybí. */
  name?: string | null;
}

/** Navigace do Map Google, Apple Map a Waze. */
export interface AppLinks {
  google: string;
  apple: string;
  waze: string;
}

/** Cíle nabídky „Navigovat". */
export interface MapLinks {
  /** Navigace v mapových aplikacích; null = není podle čeho hledat. */
  apps: AppLinks | null;
  /**
   * Odkaz na mapu, který správce zadal místo textu místa – otevře se tak,
   * jak je. Jen bez platných souřadnic (s nimi se naviguje přesně na bod);
   * jinak null.
   */
  link: string | null;
}

/** Souřadnice do URL: nejvýš 6 desetinných míst (~10 cm), bez koncových nul. */
const formatCoordinate = (value: number): string =>
  String(Number(value.toFixed(6)));

/** Doména odkazu bez „www."; "" = hodnota není platná URL. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
}

/**
 * Odkaz na mapu místo textu místa: celá http(s) URL bez mezer, kterou jde
 * přečíst; jinak null. Formulář střelnic ho výslovně povoluje („Zadej místo
 * nebo odkaz na mapu") a závod ho přebírá do mapUrl. Jako dotaz pro
 * vyhledávání se nehodí – mapová aplikace by v něm nic nenašla.
 */
export function directMapUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!/^https?:\/\/\S+$/i.test(text)) return null;
  return hostOf(text) ? text : null;
}

/**
 * Text pro vyhledání místa: query, jinak název. Hodnota, která je celá URL
 * (odkaz na mapu), se jako dotaz nehodí a přeskakuje se.
 */
function searchText(place: MapPlace): string {
  const candidates = [place.query, place.name];
  for (let i = 0; i < candidates.length; i++) {
    const value = candidates[i];
    const text = typeof value === "string" ? value.trim() : "";
    if (text && !/^https?:\/\//i.test(text)) return text;
  }
  return "";
}

/**
 * Cíle „Navigovat". Se souřadnicemi navigace v Mapách Google, Apple Mapách
 * a Waze přesně na bod. Bez nich text místa (aplikace ho dohledá) a navíc
 * odkaz na mapu, když ho správce zadal místo textu – hledání podle samotného
 * názvu může u víceznačných míst („Ostrov") navést jinam. Bez souřadnic,
 * textu i odkazu null. Všechno jsou https odkazy – v prohlížeči i ve WebView
 * obalu (target=_blank → systém) je otevře nainstalovaná aplikace, jinak web.
 */
export function mapLinks(place: MapPlace): MapLinks | null {
  const { lat, lng } = place;
  if (isValidLatLng(lat, lng)) {
    const ll = `${formatCoordinate(lat as number)},${formatCoordinate(
      lng as number
    )}`;
    return {
      apps: {
        google: `https://www.google.com/maps/dir/?api=1&destination=${ll}`,
        apple: `https://maps.apple.com/?daddr=${ll}`,
        waze: `https://waze.com/ul?ll=${ll}&navigate=yes`,
      },
      link: null,
    };
  }
  const link = directMapUrl(place.query);
  const text = searchText(place);
  if (!text && !link) return null;
  const q = encodeURIComponent(text);
  return {
    apps: text
      ? {
          google: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
          apple: `https://maps.apple.com/?daddr=${q}`,
          waze: `https://waze.com/ul?q=${q}&navigate=yes`,
        }
      : null,
    link,
  };
}

export type MapApp = "link" | "apple" | "google" | "waze";

export interface NavigationOption {
  app: MapApp;
  label: string;
  /** Doplňující řádek – u odkazu na mapu doména, kam vede. */
  description?: string;
  url: string;
}

/**
 * Nabídka „Navigovat" podle platformy: nejdřív odkaz na mapu od správce
 * (když je – přesnější než hledání podle názvu), pak na iOS Apple Mapy,
 * všude Mapy Google a Waze.
 */
export function navigationOptions(
  links: MapLinks,
  ua: string = currentUserAgent()
): NavigationOption[] {
  const options: NavigationOption[] = [];
  if (links.link) {
    const option: NavigationOption = {
      app: "link",
      label: "Odkaz na mapu",
      url: links.link,
    };
    const host = hostOf(links.link);
    if (host) option.description = host;
    options.push(option);
  }
  const apps = links.apps;
  if (apps) {
    if (isIOS(ua)) {
      options.push({ app: "apple", label: "Apple Mapy", url: apps.apple });
    }
    options.push({ app: "google", label: "Mapy Google", url: apps.google });
    options.push({ app: "waze", label: "Waze", url: apps.waze });
  }
  return options;
}
